import { CloudOff } from 'lucide-react'
import { Component, type ReactNode } from 'react'
import { navigate } from '../router'

/**
 * Catches a lazily loaded chunk that can't be fetched — offline, evicted from the caches, or
 * removed by a deploy — and shows `fallback` instead of taking the whole page down. The home
 * screen's own chunks are precached by the service worker; this covers the rest (the map
 * preview, the map and Studio routes). A failed module stays failed in this document (the
 * browser caches the failure), so the fallback holds until the next open.
 */
export class ChunkBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}

/** Full-page fallback for a route whose code couldn't load (the map and Studio need the network once). */
export function RouteUnavailable({ what }: { what: string }) {
  return (
    <div className="fc-state" role="alert">
      <div className="state-card">
        <CloudOff size={30} strokeWidth={1.8} aria-hidden />
        <h2>{what} needs a connection</h2>
        <p>It opens once you're back online. Your forecast is still here.</p>
        <div className="state-actions">
          <button type="button" className="pill is-primary" onClick={() => navigate('/')}>
            Back to the forecast
          </button>
        </div>
      </div>
    </div>
  )
}
